import { z } from 'zod';

import { ActionStatus } from 'further-types/lp/action';
import { AllocationRule } from 'further-types/lp/allocations';
import {
  FundingSource,
  InvestmentRevaluationOption,
} from 'further-types/lp/investment';
import { FxDataFeed } from 'further-types/lp/vehicle';
import { round, sumBy } from 'lodash';
import { CalendarDay } from '../../../utils';
import isMongoId from '../../helpers/is-mongo-id';
import { allocationMatchesCheckAmount } from '../../helpers/transaction-allocations-input';
import withPaginationParams from '../../helpers/with-pagination-params';
import { CreateAllocation } from '../allocations';
import { ExistingAssetSchema, NewAssetSchema } from '../asset';
import { HoldingClassFormValues, HoldingClassSchema } from '../holding-class';
import {
  ExtendendIndividualTransaction,
  InvestmentHistorySchema,
  NewStandardRevaluationSchema,
  RevaluationSchema,
} from '../revaluation';
import { IndividualTransaction } from '../transaction';

const validateHoldingClassesCurrency = (
  holdingClasses: Array<HoldingClassFormValues>,
  ctx: z.RefinementCtx,
) => {
  if (holdingClasses.length <= 1) return;

  const firstCurrency = holdingClasses[0].currency;

  const allSameCurrency = holdingClasses.every(
    (hc) => hc.currency === firstCurrency,
  );

  if (!allSameCurrency) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'All holding classes must have the same currency.',
      path: [],
    });
  }
};

export const CreateInvestmentSchema = z
  .object({
    investmentId: z.string().optional(),
    vehicleId: z.string().refine(isMongoId(), {
      message: 'Invalid vehicle ID',
    }),
    dealName: z.string().min(1, 'Deal name is required'),
    dealCompletionDate: z.instanceof(CalendarDay).optional(),
    status: z.nativeEnum(ActionStatus),
    fundingSource: z.nativeEnum(FundingSource),
    createdNewAsset: z.boolean().optional(),
    createdNewHoldingClass: z.boolean().optional(),
    allocationRule: z.nativeEnum(AllocationRule).optional(),
    revaluationOption: z.nativeEnum(InvestmentRevaluationOption).optional(),
    newRevaluations: z.array(NewStandardRevaluationSchema).optional(),
    revaluations: z.array(RevaluationSchema).optional(),
    investmentHistory: z.array(InvestmentHistorySchema).optional(),
    revaluationTransactions: z
      .record(z.string(), z.array(ExtendendIndividualTransaction))
      .optional(),
    currencyRate: z
      .object({
        fetchedRate: z.number().optional(),
        fetchedSource: z.nativeEnum(FxDataFeed).optional(),
        overrideRate: z.number().optional(),
        overrideSource: z.string().optional(),
      })
      .optional(),
    revaluationCurrencyRates: z
      .record(
        z.string(),
        z
          .object({
            fetchedRate: z.number().optional(),
            fetchedSource: z.nativeEnum(FxDataFeed).optional(),
            overrideRate: z.number().optional(),
            overrideSource: z.string().optional(),
          })
          .optional(),
      )
      .optional(),
    allocations: z.array(CreateAllocation).optional(),
    transactions: z.array(IndividualTransaction).optional(),
    holdingClasses: z
      .array(HoldingClassSchema)
      .min(1, { message: 'At least one holding class is required' })
      .superRefine(validateHoldingClassesCurrency),
  })
  .and(
    z.discriminatedUnion('isNewAsset', [NewAssetSchema, ExistingAssetSchema]),
  )
  .refine(
    (data) =>
      allocationMatchesCheckAmount(
        round(
          sumBy(data.holdingClasses, (hc) => hc.investmentAmountTcy),
          2,
        ),
        data.allocations ?? [],
      ),
    {
      message: 'Total allocation amount must match input amount',
      path: ['allocations'],
    },
  );

export type CreateInvestmentFormValues = z.infer<typeof CreateInvestmentSchema>;

export const ListInvestmentsSchema = withPaginationParams(
  z.object({
    vehicleId: z.string().refine(isMongoId(), {
      message: 'Invalid vehicle ID',
    }),
    searchQuery: z.string().optional(),
    dateRange: z
      .object({
        startDate: z.instanceof(CalendarDay),
        endDate: z.instanceof(CalendarDay),
      })
      .optional(),
    status: z.nativeEnum(ActionStatus).optional(),
  }),
  [
    'ledgerDate',
    'dateRange',
    'status',
    'searchQuery',
    'dealName',
    'totalInvestmentAmountTcy',
    'units',
    'averageUnitPriceTcy',
  ],
);
